import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router'
import { Loader2, Plus, X } from 'lucide-react'

import { Button } from '@/components/ui/button'
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/utils'
import { useDebouncedValue } from '../hooks/useDebouncedValue'

export interface PickableObject {
  id: string
  name?: string
  description?: string
}

export interface ObjectPickerProps<T extends PickableObject> {
  /** Query key prefix — the search term is appended to it. */
  queryKey: string
  /** Runs on every (debounced) search term, including the empty one. */
  search: (term: string) => Promise<T[]>
  selected: T[]
  onChange: (selected: T[]) => void
  /** Label of the button that opens the search. */
  addLabel: string
  searchPlaceholder?: string
  /** Shown in place of the selected list while nothing is selected. */
  emptyText: string
  /** Extra line under a selected row, e.g. a site's URL. */
  renderMeta?: (item: T) => React.ReactNode
  /** Extra controls shown beside the add button. */
  actions?: React.ReactNode
  /** Makes a selected row's name a link to the object. */
  href?: (item: T) => string
  disabled?: boolean
  className?: string
}

/**
 * Searches the API for objects and keeps the picked ones as a list. Used
 * wherever a field holds references to other objects — targeting, blocklists,
 * anywhere a "pick some sites" control is needed.
 */
export function ObjectPicker<T extends PickableObject>({
  queryKey,
  search,
  selected,
  onChange,
  addLabel,
  searchPlaceholder,
  emptyText,
  renderMeta,
  actions,
  href,
  disabled,
  className,
}: ObjectPickerProps<T>) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [term, setTerm] = useState('')
  const debouncedTerm = useDebouncedValue(term)

  const { data: results = [], isFetching } = useQuery({
    queryKey: [queryKey, debouncedTerm],
    queryFn: () => search(debouncedTerm),
    enabled: open,
    staleTime: 60 * 1000,
  })

  const selectedIds = new Set(selected.map((item) => item.id))
  const toggle = (item: T) =>
    onChange(
      selectedIds.has(item.id) ? selected.filter((s) => s.id !== item.id) : [...selected, item]
    )

  return (
    <div className={cn('flex flex-col gap-3', className)}>
      <div className="flex items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
              <Button type="button" variant="outline" size="sm" disabled={disabled}>
                <Plus className="size-4" />
                {addLabel}
              </Button>
            </PopoverTrigger>
            <PopoverContent align="start" className="w-80 p-0">
              {/* The API does the matching, so cmdk must not filter as well. */}
              <Command shouldFilter={false}>
                <CommandInput
                  value={term}
                  onValueChange={setTerm}
                  placeholder={searchPlaceholder ?? t('common.search')}
                />
                <CommandList>
                  {isFetching && (
                    <div className="flex items-center justify-center gap-2 py-4 text-xs text-muted-foreground">
                      <Loader2 className="size-3.5 animate-spin" />
                      {t('common.loading')}
                    </div>
                  )}
                  {!isFetching && <CommandEmpty>{t('targeting.noResults')}</CommandEmpty>}
                  {results.map((item) => (
                    <CommandItem
                      key={item.id}
                      value={item.id}
                      onSelect={() => toggle(item)}
                      className={cn(selectedIds.has(item.id) && 'text-muted-foreground')}
                    >
                      {/* The name identifies a row; the meta line under it tells
                        two same-named rows apart, and is all there is to go on
                        for an object that carries no name at all. */}
                      <span className="flex min-w-0 flex-col">
                        <span className="truncate">{item.name || item.id}</span>
                        {renderMeta && (
                          <span className="truncate text-xs text-muted-foreground">
                            {renderMeta(item)}
                          </span>
                        )}
                      </span>
                      {selectedIds.has(item.id) && (
                        <span className="ml-auto text-xs">{t('targeting.selected')}</span>
                      )}
                    </CommandItem>
                  ))}
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
          {actions}
        </div>
        {selected.length > 0 && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={disabled}
            onClick={() => onChange([])}
          >
            {t('targeting.removeAll')}
          </Button>
        )}
      </div>

      {selected.length === 0 ? (
        <p className="rounded-md border border-dashed p-3 text-xs text-muted-foreground">
          {emptyText}
        </p>
      ) : (
        <ul className="divide-y rounded-md border">
          {selected.map((item) => (
            <li key={item.id} className="flex items-center gap-2 px-3 py-2">
              <div className="min-w-0 flex-1">
                {href ? (
                  <Link to={href(item)} className="block truncate text-sm text-(--ad-link)">
                    {item.name || item.id}
                  </Link>
                ) : (
                  <div className="truncate text-sm">{item.name || item.id}</div>
                )}
                {renderMeta && (
                  <div className="truncate text-xs text-muted-foreground">{renderMeta(item)}</div>
                )}
              </div>
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                aria-label={t('common.remove')}
                disabled={disabled}
                onClick={() => onChange(selected.filter((s) => s.id !== item.id))}
              >
                <X className="size-4" />
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
